import { supabase } from "./supabase";

export type ExerciceSituation =
  | "nouvelle"
  | "existante_historique"
  | "existante_sans_historique"
  | "reprise_en_cours";

export type ExerciceStatut = "ouvert" | "fige" | "cloture" | "reouvert";

export interface ExerciceComptable {
  id: string;
  dossier_id: string;
  date_debut: string;
  date_fin: string;
  situation: ExerciceSituation;
  premier_exercice_woahda: boolean;
  exercice_long: boolean;
  soldes_importes: boolean;
  fec_importe: boolean;
  cloture: boolean;
  date_cloture: string | null;
  cloture_par: string | null;
  created_at: string;
  updated_at: string;
  /** Présent si colonnes migration clôture (116+) appliquées. */
  statut?: string | null;
  date_fige?: string | null;
  fige_par?: string | null;
  an_genere?: boolean | null;
  resultat_net?: number | null;
}

export function exerciceStatutEffectif(e: {
  statut?: string | null;
  cloture?: boolean | null;
}): ExerciceStatut {
  const s = (e.statut ?? "").trim();
  if (s === "ouvert" || s === "fige" || s === "cloture" || s === "reouvert") return s;
  return e.cloture ? "cloture" : "ouvert";
}

/** Saisie / imports autorisés (exercice réouvert après déclôture). */
export function isExerciceStatutModifiable(statut: ExerciceStatut): boolean {
  return statut === "ouvert" || statut === "reouvert";
}

export function findExerciceContenantDate(
  exercices: ExerciceComptable[],
  dateIso: string
): ExerciceComptable | null {
  const d = dateIso.slice(0, 10);
  return exercices.find((ex) => ex.date_debut <= d && ex.date_fin >= d) ?? null;
}

/** Exercice immédiatement avant `ex` (tri par date_debut), même dossier. */
export function findExercicePrecedent(
  exercices: ExerciceComptable[],
  ex: ExerciceComptable | null
): ExerciceComptable | null {
  if (!ex) return null;
  const sorted = [...exercices].sort((a, b) => a.date_debut.localeCompare(b.date_debut));
  const i = sorted.findIndex((e) => e.id === ex.id);
  if (i <= 0) return null;
  return sorted[i - 1] ?? null;
}

/** Bloque insert/update/delete d’écritures si l’exercice couvrant la date est figé ou clôturé. */
export async function assertEcritureModifiablePourDate(
  dossierId: string,
  dateEcriture: string
): Promise<void> {
  const d = dateEcriture.slice(0, 10);
  const { data: ex, error } = await supabase
    .from("exercices_comptables")
    .select("statut, cloture")
    .eq("dossier_id", dossierId)
    .lte("date_debut", d)
    .gte("date_fin", d)
    .maybeSingle();
  if (error) throw error;
  if (!ex) return;
  const st = exerciceStatutEffectif(ex as ExerciceComptable);
  if (!isExerciceStatutModifiable(st)) {
    throw new Error("Cet exercice est verrouillé");
  }
}

export async function assertEcritureModifiablePourEcritureId(ecritureId: string): Promise<void> {
  const { data: ec, error } = await supabase
    .from("ecritures_comptables")
    .select("dossier_id, date_ecriture")
    .eq("id", ecritureId)
    .maybeSingle();
  if (error) throw error;
  if (!ec) throw new Error("Écriture introuvable.");
  await assertEcritureModifiablePourDate(ec.dossier_id as string, ec.date_ecriture as string);
}

/** Import balance / FEC : même règle que la saisie. */
export async function assertExerciceModifiablePourImport(exerciceId: string): Promise<void> {
  const { data: ex, error } = await supabase
    .from("exercices_comptables")
    .select("statut, cloture")
    .eq("id", exerciceId)
    .maybeSingle();
  if (error) throw error;
  if (!ex) throw new Error("Exercice introuvable.");
  const st = exerciceStatutEffectif(ex as ExerciceComptable);
  if (!isExerciceStatutModifiable(st)) {
    throw new Error("Cet exercice est verrouillé : l’import n’est pas autorisé.");
  }
}

export type CreateExerciceInput = {
  date_debut: string;
  date_fin: string;
  situation: ExerciceSituation;
  premier_exercice_woahda?: boolean;
  exercice_long?: boolean;
  soldes_importes?: boolean;
  fec_importe?: boolean;
};

export type UpdateExerciceInput = Partial<
  Pick<
    ExerciceComptable,
    | "date_debut"
    | "date_fin"
    | "situation"
    | "premier_exercice_woahda"
    | "exercice_long"
    | "soldes_importes"
    | "fec_importe"
    | "cloture"
    | "date_cloture"
    | "cloture_par"
  >
>;

export function calculerDateCloture(dateCreation: string, exerciceLong: boolean): string {
  const start = new Date(`${dateCreation}T00:00:00`);
  if (Number.isNaN(start.getTime())) return dateCreation;

  const year = start.getFullYear();
  const june30 = new Date(year, 5, 30); // 30/06
  const isAfterJune30 = start > june30;

  if (isAfterJune30 && exerciceLong) {
    return `${year + 1}-12-31`;
  }
  return `${year}-12-31`;
}

export async function fetchExercice(dossierId: string): Promise<ExerciceComptable | null> {
  const { data, error } = await supabase
    .from("exercices_comptables")
    .select("*")
    .eq("dossier_id", dossierId)
    .order("date_debut", { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error) throw error;
  return data;
}

export async function fetchExercices(dossierId: string): Promise<ExerciceComptable[]> {
  const { data, error } = await supabase
    .from("exercices_comptables")
    .select("*")
    .eq("dossier_id", dossierId)
    .order("date_debut", { ascending: false });

  if (error) throw error;
  return data ?? [];
}

export async function createExercice(
  dossierId: string,
  data: CreateExerciceInput
): Promise<ExerciceComptable> {
  const payload = {
    dossier_id: dossierId,
    date_debut: data.date_debut,
    date_fin: data.date_fin,
    situation: data.situation,
    premier_exercice_woahda: data.premier_exercice_woahda ?? true,
    exercice_long: data.exercice_long ?? false,
    soldes_importes: data.soldes_importes ?? false,
    fec_importe: data.fec_importe ?? false,
    updated_at: new Date().toISOString(),
  };

  const { data: created, error } = await supabase
    .from("exercices_comptables")
    .insert(payload)
    .select("*")
    .single();

  if (error) throw error;
  return created;
}

const UPDATE_EXERCICE_KEYS = [
  "date_debut",
  "date_fin",
  "situation",
  "premier_exercice_woahda",
  "exercice_long",
  "soldes_importes",
  "fec_importe",
  "cloture",
  "date_cloture",
  "cloture_par",
] as const satisfies readonly (keyof UpdateExerciceInput)[];

function pickUpdatePayload(data: UpdateExerciceInput): Record<string, unknown> {
  const patch: Record<string, unknown> = {};
  for (const key of UPDATE_EXERCICE_KEYS) {
    const v = data[key];
    if (v !== undefined) patch[key] = v;
  }
  return patch;
}

export async function updateExercice(
  exerciceId: string,
  data: UpdateExerciceInput
): Promise<ExerciceComptable> {
  const patch = pickUpdatePayload(data);
  patch.updated_at = new Date().toISOString();

  const { data: updated, error } = await supabase
    .from("exercices_comptables")
    .update(patch)
    .eq("id", exerciceId)
    .select("*")
    .single();

  if (error) throw error;
  return updated;
}
